import moment from 'moment';
import { ITimeslotFilter, SubsetOfTimePillGroup } from '../types/Availability';

export type Moment = ReturnType<typeof moment>;

/**
 * Tranforms one moment into an array of moments representing each
 * day of the given month
 *
 * @param m A moment that represents the month to use
 * @returns An Array of all the days in the month as moment objects
 */
export const getDateMomentsForMonth = (m: Moment) => {
  const month = moment(m);
  const startMonth = month.clone().startOf('month');
  const endMonth = month.clone().endOf('month').add(1, 'second');
  const numDaysInMonth = endMonth.diff(startMonth, 'days');
  return (new Array(numDaysInMonth)).fill(1).map(
    (_, i) => month.clone().date(i + 1),
  );
};

export const getGoingFastDates = (
  month: Moment,
  enabledDates: Record<string, boolean>,
  nowTime?: Moment,
): string[] => {
  const NUM_CHOOSE_DAYS_FROM_MONTH = 1;
  const NUM_CHOOSE_DAYS_FROM_WEEKENDS = 4;
  const chosenDatesThisMonth = [];
  const chosenWeekendDates = [];

  const now = moment(nowTime?.format('YYYY-MM-DD')).startOf('day');

  getDateMomentsForMonth(month).some(date => {
    const formattedDate = date.format('YYYY-MM-DD');

    // Only check enabled days that are today or in the future
    if (enabledDates[formattedDate]
      && now.isSameOrBefore(date, 'day')
    ) {
      // Choose any date from the month first
      if (date.month() === now.month()
        && chosenDatesThisMonth.length < NUM_CHOOSE_DAYS_FROM_MONTH
      ) {
        chosenDatesThisMonth.push(formattedDate);
      } else if (( // Then choose up to 4 weekend dates
        chosenWeekendDates.length < NUM_CHOOSE_DAYS_FROM_WEEKENDS
      ) && (
        date.weekday() === 5 // FRI
        || date.weekday() === 6 // SAT
        || date.weekday() === 0 // SUN
      ) && (date.diff(now, 'days') < 30)) {
        chosenWeekendDates.push(formattedDate);
      }
    }
    // Break the iteration if we've fulfilled all possible going fast days
    // (Any 1 day from the month and up to 4 weekend dates from the month)
    return (chosenDatesThisMonth.length >= NUM_CHOOSE_DAYS_FROM_MONTH
      && chosenWeekendDates.length >= NUM_CHOOSE_DAYS_FROM_WEEKENDS);
  });

  return [
    ...chosenDatesThisMonth,
    ...chosenWeekendDates,
  ];
};

export const getShowSlots = <T extends SubsetOfTimePillGroup<'date' | 'slots'> & ITimeslotFilter> (
  providerWorkSchedule: { slots: number[] }[],
  group: T,
): T & { showSlots: number[]; unavailableSlots: number[]; } => {
  let scheduleSlots: number[] = (
    providerWorkSchedule?.[moment(group.date).day()]?.slots || []
  );
  scheduleSlots = scheduleSlots.filter(group.isInRange);
  scheduleSlots = scheduleSlots.filter(group.isInFuture);

  const availableSlots = group.slots.concat();
  const unavailableSlots = scheduleSlots
    .filter(slot => (
      availableSlots.indexOf(slot) === -1
    ));
  const allSlots = availableSlots.concat(scheduleSlots)
    .sort((a, b) => a - b)
    .filter((slot, index, self) => (
      // uniq()
      self.indexOf(slot) === index
    ));

  const removeThreeOutOfFourBookedSlots = () => {
    const slotsKeptSoFar = [];
    return (slot: number) => {
      const available = availableSlots.indexOf(slot) !== -1;
      const nextThreeSlots = [slot + 1, slot + 2, slot + 3];
      const nextAvailableSlots = (
        availableSlots.filter(a => nextThreeSlots.indexOf(a) !== -1)
      );

      const previousThreeSlots = [slot - 1, slot - 2, slot - 3];
      const previousAvailableSlots = (
        availableSlots.filter(a => previousThreeSlots.indexOf(a) !== -1)
      ).concat(
        slotsKeptSoFar.filter(a => previousThreeSlots.indexOf(a) !== -1),
      );

      if (available) {
        slotsKeptSoFar.push(slot);
        return true;
      }

      if (nextAvailableSlots.length > 0 || previousAvailableSlots.length > 0) {
        return false;
      }

      if (slotsKeptSoFar.length === 0 && slot % 4 !== 0) {
        // never make the first slot a :15 :30 or :45
        return false;
      }

      slotsKeptSoFar.push(slot);
      return true;
    };
  };

  let showSlots = allSlots.filter(removeThreeOutOfFourBookedSlots());
  const unavailableShownSlots = showSlots.filter((slot: number) => (
    unavailableSlots.indexOf(slot) !== -1
  ));

  const removeAllButTheFirstFourBookedSlots = () => (slot: number) => (
    availableSlots.indexOf(slot) !== -1 // available
        || unavailableShownSlots.indexOf(slot) < 4 // in the first two
  );

  showSlots = showSlots.filter(removeAllButTheFirstFourBookedSlots());

  return {
    ...group,
    showSlots,
    unavailableSlots: unavailableSlots.filter(n => showSlots.indexOf(n) !== -1),
  };
};

export const isInRange = (
  date: Moment,
  dateRangeMode: any,
  selectedDates: Moment[],
) => {
  if (!date || !dateRangeMode || selectedDates.length < 2) {
    return false;
  }

  return date.isBetween(selectedDates[0], selectedDates[1]);
};

export const isLastInRange = (
  date: Moment,
  dateRangeMode: any,
  selectedDates: Moment[],
) => {
  if (!date || !dateRangeMode || selectedDates.length < 2) {
    return false;
  }
  return date.isSame(selectedDates[selectedDates.length - 1].startOf('day'));
};

export const getWeek = (date: Moment, adjustForEarlyDays: boolean = true) => {
  const startOfMonth = date.clone().startOf('month');
  // Adjustment for years with 53 weeks
  const extraWeekAdjustment = 52;
  let startingRow = 1;
  // If the first day of the month is Sunday, Monday or Tuesday,
  // start the dates on the second row so the month header has space
  if (adjustForEarlyDays && startOfMonth.day() <= 2) {
    startingRow = 2;
  }
  // Returns negative for years with 53rd week
  let week = date.week() - startOfMonth.week() + startingRow;
  if (week < 0) {
    week += extraWeekAdjustment;
  }

  return week;
};
